import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';

import { compensationsShape, customerShape, journeyShape, passengersShape } from 'constants/propTypes';
import { Row } from 'components/shared/Layout';
import Button from 'components/ui/Button';
import { EVOUCHER_DATE_FORMAT, MAX_VOUCHER_EXPIRY_DATE, VOUCHER_LANGUAGES, codes as paymentCodes } from 'constants/payment';
import { voucherExpiryDateInvalid, getPassengersByUniqueEmails } from 'components/ui/Payments/Payments.utils';
import AlertBox from 'components/ui/AlertBox';
import EmailLanguage from 'components/ui/EmailLanguage';
import { REFUND_100, REFUND_30, REFUND_60, REFUND_75 } from 'constants/prrRefund';
import StyledReactSelect from 'components/ui/StyledReactSelect';
import TextInput from 'components/ui/TextInput';

import ServiceTypeCodes from '../../constants/serviceTypeCodes';
import { voucherCodes } from '../../constants/vouchers';

import CompensationBreakdown from './CompensationBreakdown/CompensationBreakdown';
import RefundAmountSelection from './RefundAmountSelection';
import {
  getTotalPrice,
  getTotalPoints,
  getRefundAmount,
  getLoyaltyTicketRefundAmount,
  getLoyaltyTicketsRefundAmount,
  isThroughFareWithSN,
  getRefundOptions
} from './PrrRefund.utils';
import { StyledLabel, Spacer } from './PrrRefund.styles';

const PrrRefundVoucher = ({
  onSubmit,
  loading,
  filteredProducts,
  compensateLegs,
  compensationItems = [],
  passengers,
  customer,
  reference,
  itemRefs,
  currency,
  legs
}) => {
  const serviceTypes = compensateLegs.map((leg) => leg.serviceType);

  const totalPriceInPoints = getTotalPoints(filteredProducts);
  const isLoyalty = totalPriceInPoints > 0;

  const refundOptions = getRefundOptions(compensationItems, serviceTypes, [REFUND_30, REFUND_60, REFUND_75], true, isLoyalty);

  const { register, handleSubmit, watch, setValue, control, formState } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      refundPercentage: String(refundOptions?.[0]?.value ?? REFUND_30.value),
      voucherEmail: customer.email || '',
      voucherExpiry: MAX_VOUCHER_EXPIRY_DATE,
      voucherLanguage: ''
    }
  });

  const { errors } = formState;

  const { refundPercentage, voucherExpiry, voucherLanguage, voucherEmail } = watch();

  const allPassengers = getPassengersByUniqueEmails(customer, passengers);
  const passengerOptions = allPassengers.map(({ email }) => ({ label: email, value: email }));

  const refundDisabled = !formState.isValid || loading;

  const isRefundDisabled = totalPriceInPoints && (currency === 'USD' || filteredProducts.find((p) => p.currency !== currency));
  const isESThroughFareWithSN = isThroughFareWithSN({ serviceTypes });
  const isERThroughFareWithSN = isThroughFareWithSN({ serviceTypes, service: ServiceTypeCodes.GREENSPEED });

  const onHandleCalendarChange = (event) => {
    setValue('voucherExpiry', event.target.value);
  };

  const onSubmitForm = () => {
    const { firstName, lastName } = allPassengers.find((pax) => pax.email === voucherEmail);

    let variables = {};
    const defaultVariables = {
      language: voucherLanguage,
      associatedPNR: reference,
      validUntil: moment(voucherExpiry, EVOUCHER_DATE_FORMAT).toISOString(),
      recipientEmail: voucherEmail,
      firstName,
      lastName
    };

    if (isESThroughFareWithSN || isERThroughFareWithSN) {
      const legTotalPrice = compensateLegs.reduce((acc, { products }) => acc + getTotalPrice(products), 0);
      const legTotalPriceInPoints = compensateLegs.reduce((acc, { products }) => acc + getTotalPoints(products), 0);
      const legTotalPoints = compensateLegs.reduce(
        (acc, { products }) => acc + getLoyaltyTicketsRefundAmount(products, refundPercentage),
        0
      );

      const legRefundAmount = legTotalPriceInPoints ? legTotalPoints : getRefundAmount(legTotalPrice, refundPercentage);
      const type = isESThroughFareWithSN ? voucherCodes.ES_COMPENSATION : voucherCodes.ER_COMPENSATION;

      variables = {
        ...defaultVariables,
        type,
        amount: legRefundAmount,
        currency,
        additionalDetails: compensateLegs
          .map((leg) =>
            itemRefs
              .filter((ref) => leg.products.some(({ itemRef }) => itemRef === ref))
              .map((ref) => {
                const product = leg.products.find(({ itemRef }) => itemRef === ref);

                return {
                  itemRefId: Number(ref),
                  key: `PRR_Compensation_${refundPercentage}%`,
                  value: legTotalPriceInPoints
                    ? parseFloat(
                        getLoyaltyTicketRefundAmount(leg.products.find((p) => p.itemRef === ref)?.bucketCode, refundPercentage, currency)
                      ).toFixed(2)
                    : String(getRefundAmount(legTotalPriceInPoints ? product?.loyaltyPoints ?? 0 : product?.price ?? 0, refundPercentage))
                };
              })
          )
          ?.flat()
      };
    } else {
      if (compensationItems.length > 0) {
        let refundAmount = 0;
        let type = voucherCodes.ES_COMPENSATION;

        if (compensateLegs?.length > 0 && compensateLegs[0].serviceType === ServiceTypeCodes.GREENSPEED) {
          type = voucherCodes.ER_COMPENSATION;
        }

        compensationItems.forEach(({ ticketPrice, voucherCompensationAmount }) => {
          const amount = refundPercentage === String(REFUND_100.value) ? ticketPrice : voucherCompensationAmount;

          refundAmount += amount;
        });

        variables = {
          ...defaultVariables,
          type,
          amount: refundAmount,
          currency: compensationItems[0].currency,
          additionalDetails: compensationItems.map(({ ticketNumber, voucherCompensationAmount, ticketPrice }) => {
            const product = filteredProducts.find(({ ticketNumber: productTicketNumber }) => productTicketNumber === ticketNumber);

            return {
              itemRefId: Number(product.itemRef),
              key: `PRR_Compensation_${refundPercentage}%`,
              value: String(refundPercentage === String(REFUND_100.value) ? ticketPrice : voucherCompensationAmount)
            };
          }),
          ...(compensationItems.length > 0 && {
            compensationRecordsToUpdate: compensationItems.map(({ compensationRecordId, caseId }) => ({
              compensationRecordId,
              caseId
            }))
          })
        };
      } else {
        variables = [];

        compensateLegs.forEach((leg) => {
          const legTotalPrice = getTotalPrice(leg.products);
          const legTotalPriceInPoints = getTotalPoints(leg.products);
          const legRefundAmount = legTotalPriceInPoints
            ? getLoyaltyTicketsRefundAmount(leg.products, refundPercentage)
            : getRefundAmount(legTotalPrice, refundPercentage);
          let type = null;

          if (
            leg.serviceType === ServiceTypeCodes.GREENSPEED ||
            leg.serviceType === ServiceTypeCodes.THALYS ||
            (leg.serviceType === ServiceTypeCodes.SNCF &&
              legs.some((l) => leg.isThroughFare && l.direction === leg.direction && l.serviceType.code === ServiceTypeCodes.GREENSPEED))
          ) {
            type = voucherCodes.ER_COMPENSATION;
          } else {
            type = voucherCodes.ES_COMPENSATION;
          }

          variables.push({
            ...defaultVariables,
            type,
            amount: legRefundAmount,
            currency,
            additionalDetails: itemRefs
              .filter((ref) => leg.products.some(({ itemRef }) => itemRef === ref))
              .map((ref) => {
                const product = leg.products.find(({ itemRef }) => itemRef === ref);

                return {
                  itemRefId: Number(ref),
                  key: `PRR_Compensation_${refundPercentage}%`,
                  value: legTotalPriceInPoints
                    ? parseFloat(
                        getLoyaltyTicketRefundAmount(leg.products.find((p) => p.itemRef === ref)?.bucketCode, refundPercentage, currency)
                      ).toFixed(2)
                    : String(getRefundAmount(legTotalPriceInPoints ? product?.loyaltyPoints ?? 0 : product?.price ?? 0, refundPercentage))
                };
              })
          });
        });
      }
    }

    onSubmit({ method: paymentCodes.EVOUCHER, variables });
  };

  return (
    <div>
      <h2>
        <FormattedMessage id="prrRefund.compensationAmount" />
      </h2>

      <Row>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Row>
            <RefundAmountSelection options={refundOptions} register={register} />
          </Row>
          <>
            {isRefundDisabled ? (
              <FormattedMessage id="prrRefund.refundToVoucherCurrencyWarning" />
            ) : (
              <>
                <h2>
                  <FormattedMessage id="prrRefund.compensationTickets" />
                </h2>
                <CompensationBreakdown
                  isVoucher
                  isLoyalty={isLoyalty}
                  currency={currency}
                  compensateLegs={compensateLegs}
                  refundPercentage={refundPercentage}
                  compensationItems={compensationItems}
                />
                <div>
                  <StyledLabel>
                    <FormattedMessage id="refunds.evoucher.marketSendVoucher" />
                  </StyledLabel>
                  <Controller
                    id="voucherLanguage"
                    name="voucherLanguage"
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) => VOUCHER_LANGUAGES.includes(value)
                    }}
                    render={({ field: { name, value, onChange, onBlur } }) => (
                      <EmailLanguage
                        id="voucherLanguage"
                        name={name}
                        data-cy="create-voucher-language--dropdown"
                        placeholder={<FormattedMessage id="payments.dropdownDefault" />}
                        initialValue={value}
                        width="300px"
                        mt="8px"
                        disabled={loading}
                        onChange={(languageObject) => onChange(languageObject.value)}
                        onBlur={onBlur}
                        filterOption={({ value }) => VOUCHER_LANGUAGES.includes(value)}
                      />
                    )}
                  />
                </div>
                <div>
                  <StyledLabel>
                    <FormattedMessage id="refunds.evoucher.emailSendVoucher" />
                  </StyledLabel>
                  <Controller
                    name="voucherEmail"
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({ field: { name, value, onChange, onBlur } }) => (
                      <StyledReactSelect
                        id="voucherEmail"
                        name={name}
                        data-cy="create-voucher-email--dropdown"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder={<FormattedMessage id="payments.dropdownDefault" />}
                        value={passengerOptions.find((o) => o.value === value)}
                        options={passengerOptions}
                        onChange={(emailObject) => onChange(emailObject.value)}
                        onBlur={onBlur}
                        width="300px"
                        mt="8px"
                      />
                    )}
                  />
                </div>

                <div>
                  <StyledLabel>
                    <FormattedMessage id="refunds.evoucher.expiryDateCreate" />
                  </StyledLabel>
                  <TextInput
                    id="voucherExpiry"
                    data-cy="create-voucher-expiry"
                    type="date"
                    pattern="dd/mm/yyyy"
                    value={moment(voucherExpiry).format(EVOUCHER_DATE_FORMAT)}
                    min={moment().format(EVOUCHER_DATE_FORMAT)}
                    max={moment(MAX_VOUCHER_EXPIRY_DATE).format(EVOUCHER_DATE_FORMAT)}
                    {...register('voucherExpiry', {
                      required: true,
                      validate: (value) => !voucherExpiryDateInvalid(value),
                      onChange: onHandleCalendarChange
                    })}
                    style={{ height: 32, width: 300, marginTop: 8 }}
                  />
                  {errors.voucherExpiry && (
                    <Spacer>
                      <AlertBox variant="ERROR">
                        <FormattedMessage id="refunds.evoucher.voucherExpiryDateError" />
                      </AlertBox>
                    </Spacer>
                  )}
                </div>

                <Button data-cy="submit-prr-refund" type="submit" disabled={refundDisabled} loading={loading}>
                  <FormattedMessage id="prrRefund.processRefundCta" />
                </Button>
              </>
            )}
          </>
        </form>
      </Row>
    </div>
  );
};

PrrRefundVoucher.propTypes = {
  onSubmit: PropTypes.func,
  passengers: passengersShape,
  customer: customerShape,
  loading: PropTypes.bool,
  filteredProducts: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      passengerType: PropTypes.string,
      bucketCode: PropTypes.string,
      productFamily: PropTypes.shape({
        code: PropTypes.string
      }),
      currency: PropTypes.string,
      loyaltyPoints: PropTypes.number,
      price: PropTypes.number
    })
  ),
  reference: PropTypes.string,
  itemRefs: PropTypes.arrayOf(PropTypes.string),
  shouldUseIncreasedPercentage: PropTypes.bool,
  compensateLegs: PropTypes.arrayOf(
    PropTypes.shape({
      legName: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          itemRef: PropTypes.string,
          passengerType: PropTypes.string,
          bucketCode: PropTypes.string,
          productFamily: PropTypes.shape({
            code: PropTypes.string
          }),
          currency: PropTypes.string,
          loyaltyPoints: PropTypes.number,
          price: PropTypes.number
        })
      ),
      serviceType: PropTypes.string
    })
  ),
  compensationItems: compensationsShape,
  currency: PropTypes.string,
  legs: journeyShape
};

export default PrrRefundVoucher;
